import React, { useEffect, useMemo, useState } from "react"
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    IconButton,
    LinearProgress,
    Tooltip,
    useMediaQuery,
    useTheme
} from "@mui/material"
import {
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Close,
    ReceiptLongOutlined
} from "@mui/icons-material"
import { initials } from "../../StockManager/classroom/helpers"
import {
    MONTHS,
    formatMoney,
    monthStatus,
    paidMonthsOf,
    sumFees
} from "./helpers"

const buttonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

const STATUS_STYLE = {
    paid: "border-emerald-200 bg-emerald-50/70",
    due: "border-amber-200 bg-white hover:border-amber-300 hover:bg-amber-50/40",
    upcoming: "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
}

const STATUS_BADGE = {
    paid: "bg-emerald-100 text-emerald-700",
    due: "bg-amber-100 text-amber-700",
    upcoming: "bg-slate-100 text-slate-500"
}

function Stat({ label, value, tone = "text-slate-900" }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <div className="text-xs text-slate-500">{label}</div>
            <div className={`mt-0.5 text-base font-semibold tabular-nums ${tone}`}>{value}</div>
        </div>
    )
}

function MonthTile({ name, status, entry, selected, onToggle, onReceipt, receiptBusy }) {
    if (status === "paid") {
        return (
            <div className={`flex flex-col rounded-xl border p-3 ${STATUS_STYLE.paid}`}>
                <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-800">{name}</span>
                    <CheckCircle className="text-emerald-500" sx={{ fontSize: 20 }} />
                </div>
                <div className="mt-2 text-sm font-semibold tabular-nums text-emerald-700">
                    {formatMoney(entry.amount)}
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="text-xs tabular-nums text-slate-500">Paid {entry.date}</span>
                    <Tooltip title="Download receipt" arrow>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Download ${name} receipt`}
                                onClick={onReceipt}
                                disabled={receiptBusy}
                                sx={{ mr: -0.75, mb: -0.5 }}
                            >
                                {receiptBusy
                                    ? <CircularProgress size={16} />
                                    : <ReceiptLongOutlined fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>
        )
    }

    return (
        <button
            type="button"
            aria-pressed={selected}
            onClick={onToggle}
            className={[
                "flex flex-col rounded-xl border p-3 text-left transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                selected ? "border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400" : STATUS_STYLE[status]
            ].join(" ")}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-slate-800">{name}</span>
                <span
                    className={[
                        "flex h-5 w-5 items-center justify-center rounded-md border text-white",
                        selected ? "border-indigo-600 bg-indigo-600" : "border-slate-300 bg-white"
                    ].join(" ")}
                >
                    {selected && (
                        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </span>
            </div>
            <span className={`mt-3 w-fit rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[status]}`}>
                {status}
            </span>
        </button>
    )
}

/**
 * One student's monthly fees for a year: which months are paid, and
 * collecting any number of unpaid months in one go.
 */
export default function StudentFeeDialog({
    open,
    onClose,
    student,
    classItem,
    feeItems,
    payments,
    loading,
    collecting,
    receiptBusyMonth,
    onCollect,
    onReceipt
}) {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const currentYear = new Date().getFullYear()

    const [year, setYear] = useState(currentYear)
    const [selectedMonths, setSelectedMonths] = useState([])
    const [selectedItems, setSelectedItems] = useState([])

    // Start fresh for every student: this year, nothing picked, every fee item on
    useEffect(() => {
        if (!open) return
        setYear(currentYear)
        setSelectedMonths([])
        setSelectedItems(feeItems.map((item) => item.feeName))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, student?.id])

    // A finished collection makes the picked months paid
    useEffect(() => {
        setSelectedMonths([])
    }, [payments, year])

    const paidMonths = useMemo(() => paidMonthsOf(payments, year), [payments, year])
    const chargedItems = feeItems.filter((item) => selectedItems.includes(item.feeName))
    const perMonth = sumFees(chargedItems)
    const total = perMonth * selectedMonths.length

    const statuses = MONTHS.map((_, index) => monthStatus(index + 1, year, paidMonths))
    const paidCount = statuses.filter((status) => status === "paid").length
    const dueCount = statuses.filter((status) => status === "due").length
    const collected = Object.values(paidMonths).reduce((sum, entry) => sum + entry.amount, 0)

    const toggleMonth = (month) =>
        setSelectedMonths((months) =>
            months.includes(month) ? months.filter((m) => m !== month) : [...months, month].sort((a, b) => a - b)
        )

    const toggleItem = (feeName) =>
        setSelectedItems((items) =>
            items.includes(feeName) ? items.filter((name) => name !== feeName) : [...items, feeName]
        )

    const selectAllDue = () =>
        setSelectedMonths(statuses.map((status, index) => (status === "due" ? index + 1 : null)).filter(Boolean))

    const canCollect = !collecting && selectedMonths.length > 0 && perMonth > 0

    return (
        <Dialog
            open={open}
            onClose={collecting ? undefined : onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "18px", bgcolor: "#f8fafc" } }}
        >
            <div className="h-1">{loading && <LinearProgress sx={{ height: 3 }} />}</div>

            {/* Student */}
            <header className="flex items-start gap-3 border-b border-slate-200 bg-white px-5 py-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                    {initials(student?.name)}
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="truncate text-lg font-semibold text-slate-900">{student?.name || "Student"}</h2>
                    <p className="text-sm text-slate-500">
                        {classItem?.class_name} · Roll {student?.roll_no ?? "-"}
                    </p>
                </div>
                <IconButton aria-label="Close" onClick={onClose} disabled={collecting} sx={{ mr: -1 }}>
                    <Close />
                </IconButton>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-5">
                {/* Year and summary */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
                        <IconButton size="small" aria-label="Previous year" onClick={() => setYear(year - 1)}>
                            <ChevronLeft fontSize="small" />
                        </IconButton>
                        <span className="w-14 text-center text-sm font-semibold tabular-nums text-slate-800">{year}</span>
                        <IconButton
                            size="small"
                            aria-label="Next year"
                            onClick={() => setYear(year + 1)}
                            disabled={year >= currentYear}
                        >
                            <ChevronRight fontSize="small" />
                        </IconButton>
                    </div>
                    {dueCount > 0 && (
                        <Button size="small" onClick={selectAllDue} sx={buttonSx}>
                            Select all {dueCount} due
                        </Button>
                    )}
                </div>

                <div className="mb-5 grid grid-cols-3 gap-3">
                    <Stat label="Months paid" value={`${paidCount} / 12`} />
                    <Stat label="Collected" value={formatMoney(collected)} tone="text-emerald-700" />
                    <Stat label="Months due" value={dueCount} tone={dueCount > 0 ? "text-amber-600" : "text-slate-900"} />
                </div>

                {/* Months */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {MONTHS.map((name, index) => {
                        const month = index + 1
                        return (
                            <MonthTile
                                key={name}
                                name={name}
                                status={statuses[index]}
                                entry={paidMonths[month]}
                                selected={selectedMonths.includes(month)}
                                onToggle={() => toggleMonth(month)}
                                onReceipt={() => onReceipt({ month, year, entry: paidMonths[month] })}
                                receiptBusy={receiptBusyMonth === `${year}-${month}`}
                            />
                        )
                    })}
                </div>
            </div>

            {/* Fee items and collection */}
            <footer className="border-t border-slate-200 bg-white px-5 py-4">
                {feeItems.length === 0 ? (
                    <p className="text-sm text-slate-500">
                        No monthly fee is set for {classItem?.class_name || "this class"}. Add one in Settings → Fee structure to start collecting.
                    </p>
                ) : (
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div className="min-w-0">
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Charge per month
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {feeItems.map((item) => {
                                    const checked = selectedItems.includes(item.feeName)
                                    return (
                                        <label
                                            key={item.feeName}
                                            className={[
                                                "flex cursor-pointer items-center rounded-lg border pr-3 text-sm transition-colors",
                                                checked
                                                    ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                                                    : "border-slate-200 text-slate-500"
                                            ].join(" ")}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onChange={() => toggleItem(item.feeName)}
                                                sx={{ p: 0.75 }}
                                            />
                                            <span>{item.feeName}</span>
                                            <span className="ml-2 font-semibold tabular-nums">{formatMoney(item.feeAmount)}</span>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                            <div className="text-right">
                                <div className="text-xs text-slate-500">
                                    {selectedMonths.length} month{selectedMonths.length === 1 ? "" : "s"} × {formatMoney(perMonth)}
                                </div>
                                <div className="text-xl font-bold tabular-nums text-slate-900">{formatMoney(total)}</div>
                            </div>
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={!canCollect}
                                onClick={() => onCollect({ months: selectedMonths, year, amount: perMonth })}
                                startIcon={collecting ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{ ...buttonSx, px: 2.5, py: 1 }}
                            >
                                {collecting ? "Collecting…" : "Collect fee"}
                            </Button>
                        </div>
                    </div>
                )}
            </footer>
        </Dialog>
    )
}
